import { Stack, TableCell, TableRow, IconButton, Snackbar } from '@mui/material'
import RequestRetryAlert from 'components/atoms/RequestRetryAlert'
import DateCell from 'components/atoms/table/DateCell'
import PresetTableContainer from 'components/atoms/table/PresetTableContainer'
import UsersIconPopupList from 'components/molecules/UsersIconPopupList'
import EditCohortSessionButton from 'components/molecules/adminDashboard/cohorts/EditCohortSessionButton'
import { useEffect, useState } from 'react'
import { COHORTS_URL } from 'services/constants'
import { getRequest } from 'utils/api'
import { getGoalNameById } from 'utils/getSupportingInfo'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { SBUSCRIPTION_ADMIN } from 'routes/constants'
import EditCohortNameButton from 'components/molecules/adminDashboard/cohorts/EditCohortNameButton'
import { FacilitatedSessionsDisplay } from 'components/molecules/adminDashboard/cohorts/FacilitatedSessions'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import { deleteRequest } from 'utils/api'

const CohortManagement = ({ organizationId }) => {
  const { t } = useTranslation()
  const [cohortSessions, setCohortSessions] = useState([])
  const [requestSuccess, setRequestSuccess] = useState(undefined)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [selectedCohort, setSelectedCohort] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [requestError, setRequestError] = useState(null)

  const location = useLocation()
  const editable = location.pathname === SBUSCRIPTION_ADMIN
  const fetchCohorts = async () => {
    const res = await getRequest(COHORTS_URL, { organizationId })
    if (res.status === 200) {
      const cohortRes = res.data.data
      const sessionRes = await Promise.all(
        cohortRes.map(async r => {
          const sessions = await Promise.all(
            r.cohortSessions.map(async (s, i) => {
              const goalName = await getGoalNameById(s.goalId)
              return {
                ...s,
                index: i,
                members: i === 0 ? r.cohortMembers : [],
                cohortName: i === 0 ? r.name : undefined,
                goalName
              }
            })
          )
          return sessions
        })
      )
      const sessionsData = [].concat(...sessionRes)
      setCohortSessions(sessionsData)
      setRequestSuccess(true)
    } else {
      setRequestSuccess(false)
    }
  }
  useEffect(() => {
    fetchCohorts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId])

  const handleDeleteCohort = async cohort => {
    const res = await deleteRequest(`${COHORTS_URL}/${cohort.cohortId}`)
    if (res.status === 204) {
      await fetchCohorts()
      console.log('Deleting cohort:', cohort)
      setOpenDeleteDialog(false)
      setSnackbarOpen(true)
      setRequestError(null)
    } else {
      setRequestError(true)
    }
  }

  return (
    <Stack padding={0} spacing={5}>
      {editable && (
        <EditCohortSessionButton
          newCohort={true}
          organizationId={organizationId}
          successCallback={fetchCohorts}
        />
      )}
      <PresetTableContainer
        maxHeight='100%'
        headers={
          <>
            {editable && <TableCell />}
            <TableCell>{t('company.name')}</TableCell>
            <TableCell>{t('company.members')}</TableCell>
            <TableCell>{t('company.startDate')}</TableCell>
            <TableCell>{t('company.endDate')}</TableCell>
            <TableCell>{t('company.path')}</TableCell>
            <TableCell>{t('company.autoAssihnPath')}</TableCell>
            <TableCell>{t('company._360Reassessment')}</TableCell>
            <TableCell>{t('company.preFacilitationEmail')}</TableCell>
            <TableCell />
          </>
        }>
        {cohortSessions.map(c => (
          <TableRow key={`cohort-session-${c.id}`}>
            {editable && (
              <TableCell>
                {c.index === 0 ? (
                  <EditCohortSessionButton
                    newSession={true}
                    cohort={{ id: c.cohortId, name: c.cohortName }}
                    successCallback={fetchCohorts}
                    organizationId={organizationId}
                  />
                ) : null}
              </TableCell>
            )}
            <TableCell>
              <Stack direction='row' spacing={1} alignItems='center'>
                <span>{c.cohortName}</span>
                {editable && c.index === 0 && (
                  <>
                    <EditCohortNameButton
                      cohortSession={c}
                      successCallback={fetchCohorts}
                    />
                    <IconButton
                      onClick={() => {
                        setSelectedCohort(c)
                        setRequestError(null)
                        setOpenDeleteDialog(true)
                      }}
                      data-testid={`delete-cohort-${c.cohortId}`}
                      size='small'
                      color='muted'>
                      <DeleteForeverOutlinedIcon />
                    </IconButton>
                  </>
                )}
              </Stack>
            </TableCell>
            <TableCell>
              <UsersIconPopupList
                parentKey={c.cohortId}
                users={c.members}
                popupTitle={t('company.allMembers')}
                cohortName={c.cohortName}
              />
            </TableCell>
            <DateCell value={c.startDate} />
            <DateCell value={c.endDate} />
            <TableCell>{c.goalName}</TableCell>
            <TableCell>
              {c.autoAssignPathOnStart ? t('company.on') : t('company.off')}
            </TableCell>
            <TableCell>
              {c.resend360AfterDays
                ? `${c.resend360AfterDays} ${t('company.days')}`
                : t('company.off')}
            </TableCell>
            <TableCell>
              {c.preFacilitationEmails ? t('company.on') : t('company.off')}
            </TableCell>
            <TableCell>
              {editable ? (
                <EditCohortSessionButton
                  cohortSession={c}
                  successCallback={fetchCohorts}
                  editMode={true}
                  organizationId={organizationId}
                />
              ) : (
                <FacilitatedSessionsDisplay
                  cohortSessionId={c.id}
                  goalId={c.goalId}
                />
              )}
            </TableCell>
          </TableRow>
        ))}
      </PresetTableContainer>
      {requestSuccess === false ? <RequestRetryAlert /> : null}
      <CommonDialog
        open={openDeleteDialog}
        onClose={value => {
          if (value === 1 && selectedCohort) {
            handleDeleteCohort(selectedCohort)
          } else {
            setOpenDeleteDialog(false)
          }
        }}
        titleText={t('company.removecohort')}
        notificationText={`Are you sure to delete ${selectedCohort?.cohortName}?`}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        showCloseButton>
        {requestError && <RequestRetryAlert />}
      </CommonDialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={'Cohort deleted'}
      />
    </Stack>
  )
}

export default CohortManagement
